import { useEffect } from "react";
import { useHistory, useLocation } from "react-router";

/**
 * Seeds a list page's search box from a `?search=` query param, then strips
 * the param from the URL.
 *
 * EE-222: the global search modal caps each group at 5 hits and offers a
 * "See all N" link into the corresponding list page. Those pages keep their
 * search term in session storage (useStateWithSessionStorage), not in the
 * URL, so a plain link has no way to hand them a term. This bridges the two
 * without turning the list pages' search into URL-driven state.
 *
 * The param is stripped with history.replace for the same reason
 * /kunder/:userId is: leaving it in place means a refresh silently
 * re-applies a search the user may have since cleared.
 */
const useSearchQueryParam = (setSearch: (value: string) => void) => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const param = new URLSearchParams(location.search).get("search");
    if (param === null) return;
    setSearch(param);
    history.replace(location.pathname);
    // Keyed on location.search, not [] : react-router keeps the same
    // component mounted when a push lands on the route it is already on, so
    // a mount-only effect would ignore "See all N" whenever the user is
    // already on the destination page. Re-applying is safe because the
    // replace() above removes the param, so the next run bails at the null
    // check and an edited search box is never overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
};

export default useSearchQueryParam;
